import { useState, useEffect } from 'react';
import { API } from './constants';

const CAT_LABELS = {
  delivery_failure: 'Bounces',
  spam: 'Spam',
  out_of_office: 'Afwezig (OOO)',
  unsubscribe: 'Afmeldingen',
};

export const InboxCleanupSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => {
    fetch(`${API}/email/inbox/cleanup/settings`, { credentials: 'include' })
      .then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await fetch(`${API}/email/inbox/cleanup/settings`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    const data = await res.json();
    setSettings(data);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/email/inbox/cleanup/run`, {
        method: 'POST', credentials: 'include',
      });
      setLastRun(await res.json());
    } catch { /* ignore */ }
    setRunning(false);
  };

  const toggleCategory = (cat) => {
    setSettings(s => {
      const cats = [...(s.categories || [])];
      const i = cats.indexOf(cat);
      if (i >= 0) cats.splice(i, 1); else cats.push(cat);
      return { ...s, categories: cats };
    });
  };

  if (!settings) return null;

  return (
    <div className="mt-8 space-y-4 border-t border-zinc-200 pt-8" data-testid="inbox-cleanup-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold">Inbox Auto-Cleanup</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Automatisch oude bounces, spam en afwezigheidsmeldingen verwijderen — draait elk uur</p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-emerald-600 font-medium">Opgeslagen!</span>}
          <button onClick={handleRunNow} disabled={running} data-testid="run-cleanup-now-btn"
            className="px-3 py-2 text-xs font-medium border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors">
            {running ? 'Bezig...' : 'Nu uitvoeren'}
          </button>
          <button onClick={handleSave} disabled={saving} data-testid="save-cleanup-btn"
            className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-5 space-y-5">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!settings.enabled}
            onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
            className="w-4 h-4 accent-[#0055FF]" data-testid="cleanup-enabled-toggle" />
          <span className="text-sm font-medium">Auto-cleanup ingeschakeld</span>
          <span className="text-xs text-zinc-400">{settings.enabled ? 'Draait elk uur op de achtergrond' : 'Uitgeschakeld'}</span>
        </label>

        <div>
          <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Bewaartermijn (dagen)</label>
          <div className="flex items-center gap-3">
            <input type="number" min="1" max="365" value={settings.retention_days || 14}
              onChange={e => setSettings(s => ({ ...s, retention_days: Math.max(1, parseInt(e.target.value) || 1) }))}
              className="border border-zinc-200 px-3 py-1.5 text-sm rounded-sm w-24" data-testid="cleanup-retention-input" />
            <span className="text-xs text-zinc-500">Emails ouder dan dit aantal dagen worden verwijderd</span>
          </div>
        </div>

        <div>
          <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 block">Categorieen om op te ruimen</label>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(CAT_LABELS).map(([k, label]) => {
              const sel = (settings.categories || []).includes(k);
              return (
                <button key={k} type="button" onClick={() => toggleCategory(k)}
                  data-testid={`cleanup-cat-${k}`}
                  className={`px-3 py-1.5 text-xs border transition-colors ${sel ? 'bg-red-600 text-white border-red-600' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}>
                  {label}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-zinc-400 mt-1.5">Alleen geselecteerde categorieen worden automatisch verwijderd. Andere mails (interesse, vragen, offertes) blijven veilig staan.</p>
        </div>

        {lastRun && (
          <div className="text-xs px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="cleanup-result">
            Laatste run: <strong>{lastRun.deleted_count}</strong> emails verwijderd
            {lastRun.skipped && ' (overgeslagen — uitgeschakeld)'}
          </div>
        )}
      </div>
    </div>
  );
};
